import React, { useState, useEffect } from 'react';
import { CONFIG } from '../config';

const InboundMessagesPage = () => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [aniFilter, setAniFilter] = useState('');
  const [channelFilter, setChannelFilter] = useState('');
  const [limit, setLimit] = useState('100');
  const [expandedMessage, setExpandedMessage] = useState(null);

  useEffect(() => {
    fetchMessages();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchMessages = async () => {
    try {
      setLoading(true);
      setError(null);

      const queryParams = new URLSearchParams();
      if (aniFilter) queryParams.append('ani', aniFilter);
      if (channelFilter) queryParams.append('channel', channelFilter);
      if (limit) queryParams.append('limit', limit);

      const url = `${CONFIG.apiBaseUrl}/admin/inbound-messages${queryParams.toString() ? '?' + queryParams.toString() : ''}`;

      const resp = await fetch(url, {
        credentials: 'include',
      });

      if (!resp.ok) {
        const errorData = await resp.json();
        throw new Error(errorData.detail || `Failed to fetch: ${resp.statusText}`);
      }

      const data = await resp.json();
      setMessages(data.messages || []);
    } catch (err) {
      console.error('Error fetching inbound messages:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchMessages();
  };

  const toggleExpand = (key) => {
    setExpandedMessage(expandedMessage === key ? null : key);
  };

  const formatTimestamp = (isoString) => {
    if (!isoString) return 'N/A';
    const date = new Date(isoString);
    if (isNaN(date.getTime())) return isoString;
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  const formatPhoneNumber = (phone) => {
    if (!phone) return '';
    const cleaned = phone.replace(/\D/g, '');
    if (cleaned.length === 11) {
      return `+${cleaned[0]} (${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`;
    }
    return phone;
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'responded': return 'status-badge status-responded';
      case 'sent': return 'status-badge status-responded';
      case 'processing': return 'status-badge status-processing';
      case 'partial': return 'status-badge status-partial';
      case 'error': return 'status-badge status-error';
      case 'failed': return 'status-badge status-error';
      case 'no_lex': return 'status-badge status-no-lex';
      case 'heydj': return 'status-badge status-heydj';
      case 'completed': return 'status-badge status-responded';
      default: return 'status-badge';
    }
  };

  const getDirectionLabel = (direction) => {
    switch (direction) {
      case 'outbound': return 'Bot Reply';
      case 'platform_send': return 'Outbound';
      case 'scheduled_send': return 'Scheduled';
      case 'guest_add': return 'Guest Added';
      case 'guest_edit': return 'Guest Edited';
      case 'guest_delete': return 'Guest Deleted';
      default: return 'Inbound';
    }
  };

  const isPlatformOutbound = (direction) => {
    return direction === 'platform_send' || direction === 'scheduled_send';
  };

  const isGuestAction = (direction) => {
    return direction === 'guest_add' || direction === 'guest_edit' || direction === 'guest_delete';
  };

  return (
    <div className="whatsapp-responses-page">
      <h2 className="page-title">Messages Log</h2>

      {loading && <div className="loading-message">Loading messages...</div>}
      {error && <div className="error-message">Error: {error}</div>}

      {!loading && (
        <>
          <div className="messages-container">
            <div className="page-controls">
              <form onSubmit={handleSearch} className="filter-form">
                <div className="filter-group">
                  <label htmlFor="aniFilter">Phone Number:</label>
                  <input
                    type="text"
                    id="aniFilter"
                    value={aniFilter}
                    onChange={(e) => setAniFilter(e.target.value)}
                    placeholder="e.g., +15551234567"
                    className="form-input"
                    style={{ width: '200px' }}
                  />
                </div>
                <div className="filter-group">
                  <label htmlFor="channelFilter">Channel:</label>
                  <select
                    id="channelFilter"
                    value={channelFilter}
                    onChange={(e) => setChannelFilter(e.target.value)}
                    className="status-filter"
                  >
                    <option value="">All</option>
                    <option value="sms">SMS</option>
                    <option value="whatsapp">WhatsApp</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>
                <div className="filter-group">
                  <label htmlFor="limit">Limit:</label>
                  <select
                    id="limit"
                    value={limit}
                    onChange={(e) => setLimit(e.target.value)}
                    className="status-filter"
                  >
                    <option value="50">50</option>
                    <option value="100">100</option>
                    <option value="200">200</option>
                    <option value="500">500</option>
                  </select>
                </div>
                <button type="submit" className="btn-refresh">
                  Search
                </button>
              </form>
              <button onClick={fetchMessages} className="btn-refresh">
                Refresh
              </button>
            </div>

            {messages.length === 0 && (
              <div className="no-messages">
                No messages found. {aniFilter && 'Try a different phone number or clear the filter.'}
              </div>
            )}

            {messages.length > 0 && (
              <>
                <div className="messages-count">
                  Showing {messages.length} message{messages.length !== 1 ? 's' : ''}
                </div>
                <div className="messages-table-wrapper">
                  <table className="messages-table">
                    <thead>
                      <tr>
                        <th>Phone</th>
                        <th>Direction</th>
                        <th>Channel</th>
                        <th>Message</th>
                        <th>Status</th>
                        <th>Time</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {messages.map((msg) => {
                        const rowKey = `${msg.phone_number}_${msg.timestamp_id}`;
                        return (
                          <React.Fragment key={rowKey}>
                            <tr className={msg.direction === 'outbound' || isPlatformOutbound(msg.direction) ? 'outbound-row' : isGuestAction(msg.direction) ? 'outbound-row' : ''}>
                              <td>
                                {isPlatformOutbound(msg.direction)
                                  ? (msg.recipients && msg.recipients.length > 0
                                      ? msg.recipients.join(', ')
                                      : msg.to_phone
                                        ? formatPhoneNumber(msg.to_phone)
                                        : formatPhoneNumber(msg.phone_number))
                                  : formatPhoneNumber(msg.phone_number)}
                              </td>
                              <td>
                                <span className={`direction-badge direction-${isGuestAction(msg.direction) ? 'platform_send' : (msg.direction || 'inbound')}`}>
                                  {getDirectionLabel(msg.direction)}
                                </span>
                              </td>
                              <td>
                                <span className="message-type-badge">
                                  {msg.channel || 'unknown'}
                                </span>
                              </td>
                              <td className="message-preview">
                                {msg.message_body ? (
                                  <span>{msg.message_body.substring(0, 50)}{msg.message_body.length > 50 ? '...' : ''}</span>
                                ) : (
                                  <span className="no-text">No text content</span>
                                )}
                              </td>
                              <td>
                                <span
                                  className={getStatusBadgeClass(msg.status)}
                                  title={msg.error_detail || undefined}
                                >
                                  {msg.status || '-'}
                                </span>
                                {(msg.status === 'error' || msg.status === 'failed') && msg.error_detail && (
                                  <div style={{ fontSize: '0.75em', color: '#dc3545', marginTop: '2px', maxWidth: '180px', wordBreak: 'break-word' }}>
                                    {msg.error_detail.length > 80 ? msg.error_detail.substring(0, 80) + '…' : msg.error_detail}
                                  </div>
                                )}
                                {msg.truncated && (
                                  <div style={{ fontSize: '0.75em', color: '#b45309', marginTop: '2px' }} title="Bot reply was truncated to 500 characters for SMS">
                                    ✂ truncated
                                  </div>
                                )}
                              </td>
                              <td>{formatTimestamp(msg.created_at)}</td>
                              <td>
                                <button
                                  onClick={() => toggleExpand(rowKey)}
                                  className="btn-small btn-edit"
                                >
                                  {expandedMessage === rowKey ? 'Hide' : 'Details'}
                                </button>
                              </td>
                            </tr>
                            {expandedMessage === rowKey && (
                              <tr className="expanded-row">
                                <td colSpan="7">
                                  <div className="message-details">
                                    <h4>Message Details</h4>
                                    <div className="detail-grid">
                                      <div className="detail-item">
                                        <strong>{isPlatformOutbound(msg.direction) ? 'From Number:' : 'Phone Number:'}</strong>
                                        <span>{msg.phone_number}</span>
                                      </div>
                                      {isPlatformOutbound(msg.direction) && (msg.to_phone || (msg.recipients && msg.recipients.length > 0)) && (
                                        <div className="detail-item">
                                          <strong>To:</strong>
                                          <span>{msg.to_phone ? formatPhoneNumber(msg.to_phone) : msg.recipients.join(', ')}</span>
                                        </div>
                                      )}
                                      <div className="detail-item">
                                        <strong>Channel:</strong>
                                        <span>{msg.channel}</span>
                                      </div>
                                      <div className="detail-item">
                                        <strong>Direction:</strong>
                                        <span>{getDirectionLabel(msg.direction)}</span>
                                      </div>
                                      <div className="detail-item">
                                        <strong>Event ID:</strong>
                                        <span>{msg.event_id || 'Unknown'}</span>
                                      </div>
                                      <div className="detail-item">
                                        <strong>Status:</strong>
                                        <span>{msg.status}</span>
                                      </div>
                                      <div className="detail-item">
                                        <strong>{isPlatformOutbound(msg.direction) ? 'Sent:' : 'Received:'}</strong>
                                        <span>{formatTimestamp(msg.created_at)}</span>
                                      </div>
                                      {isPlatformOutbound(msg.direction) && msg.recipients && (
                                        <div className="detail-item">
                                          <strong>Recipients:</strong>
                                          <span>{msg.recipients.join(', ')}</span>
                                        </div>
                                      )}
                                      {isPlatformOutbound(msg.direction) && msg.sent_count != null && (
                                        <div className="detail-item">
                                          <strong>Sent Count:</strong>
                                          <span>{msg.sent_count}</span>
                                        </div>
                                      )}
                                      {isPlatformOutbound(msg.direction) && msg.created_by && (
                                        <div className="detail-item">
                                          <strong>Sent By:</strong>
                                          <span>{msg.created_by}</span>
                                        </div>
                                      )}
                                      {!isPlatformOutbound(msg.direction) && msg.lex_intent && (
                                        <div className="detail-item">
                                          <strong>Lex Intent:</strong>
                                          <span className="lex-intent-badge">{msg.lex_intent}</span>
                                        </div>
                                      )}
                                      {!isPlatformOutbound(msg.direction) && msg.lex_response && (
                                        <div className="detail-item">
                                          <strong>Lex Response:</strong>
                                          <span>{msg.lex_response}</span>
                                        </div>
                                      )}
                                      {msg.error_detail && (
                                        <div className="detail-item">
                                          <strong>Error Detail:</strong>
                                          <span style={{ color: '#dc3545' }}>{msg.error_detail}</span>
                                        </div>
                                      )}
                                      {msg.truncated && (
                                        <div className="detail-item">
                                          <strong>Note:</strong>
                                          <span style={{ color: '#b45309' }}>Bot reply was truncated to 500 characters for SMS delivery.</span>
                                        </div>
                                      )}
                                    </div>
                                    {msg.message_body && (
                                      <div className="message-full-text">
                                        <strong>Full Message:</strong>
                                        <div className="text-content">{msg.message_body}</div>
                                      </div>
                                    )}
                                    {msg.raw_data && (
                                      <div className="raw-data">
                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                                          <strong>Raw Payload:</strong>
                                          <button
                                            className="btn-small"
                                            style={{ fontSize: '0.75em', padding: '2px 8px' }}
                                            onClick={() => {
                                              navigator.clipboard.writeText(JSON.stringify(msg.raw_data, null, 2));
                                            }}
                                          >
                                            Copy
                                          </button>
                                        </div>
                                        <pre>{JSON.stringify(msg.raw_data, null, 2)}</pre>
                                      </div>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default InboundMessagesPage;
